import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { AlertTriangle, Sparkles, Check, XCircle, GitBranch, Clock, Loader2, Share2, Copy, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";

const sevBorder = { critical: "neon-border-red", high: "neon-border-amber", medium: "neon-border-yellow", low: "neon-border-blue" };

function ConfidenceBar({ score }) {
  if (score == null) return null;
  const pct = Math.round(score * 100);
  const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : pct >= 50 ? "bg-yellow-500" : "bg-cyan-500";
  return (
    <div className="flex items-center gap-2" data-testid="confidence-score">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono font-bold min-w-[32px] text-right">{pct}%</span>
    </div>
  );
}

export default function IncidentCenterPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const isViewer = user?.role === "viewer";

  const [severityFilter, setSeverityFilter] = useState(searchParams.get("severity") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [pipelineFilter, setPipelineFilter] = useState(searchParams.get("pipeline_id") || "all");

  const fetchIncidents = useCallback(async () => {
    const params = {};
    if (severityFilter !== "all") params.severity = severityFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (pipelineFilter !== "all") params.pipeline_id = pipelineFilter;
    try {
      const { data } = await api.get("/incidents", { params });
      setIncidents(data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [severityFilter, statusFilter, pipelineFilter]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const openDetail = async (inc) => {
    setSelected(inc.incident_id);
    try { const { data } = await api.get(`/incidents/${inc.incident_id}`); setDetailData(data); } catch { setDetailData(inc); }
  };

  const handleGenerateSummary = async () => {
    if (!detailData) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/incidents/${detailData.incident_id}/generate-summary`);
      setDetailData((prev) => ({ ...prev, ai_summary: data.summary }));
      toast.success("AI summary generated");
    } catch { toast.error("Failed to generate summary"); }
    finally { setAiLoading(false); }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!detailData) return;
    try {
      const { data } = await api.patch(`/incidents/${detailData.incident_id}`, { status: newStatus });
      setDetailData((prev) => ({ ...prev, ...data }));
      fetchIncidents();
      toast.success(`Incident ${newStatus}`);
    } catch { toast.error("Failed to update"); }
  };

  const handleApproveRemediation = async (actionId, action) => {
    try {
      await api.patch(`/remediations/${actionId}/${action}`, { approved_by: user?.email || "unknown", approval_note: approvalNote || `${action} by ${user?.name}` });
      setApprovalNote("");
      const { data } = await api.get(`/incidents/${detailData.incident_id}`);
      setDetailData(data);
      fetchIncidents();
      toast.success(`Remediation ${action}d`);
    } catch { toast.error("Failed to update remediation"); }
  };

  const copyShareLink = (incidentId) => {
    const url = `${window.location.origin}/report/incidents/${incidentId}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Share link copied!")).catch(() => toast.error("Copy failed"));
  };

  const pipelineIds = [...new Set(incidents.map((i) => i.pipeline_id))];

  if (loading) return (
    <div className="p-6 space-y-4" data-testid="incidents-loading">
      <Skeleton className="h-8 w-48" />
      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
    </div>
  );

  return (
    <div className="p-6 space-y-4" data-testid="incident-center-page">
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Incident Command Center</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{incidents.length} incidents</p>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="incident-filters">
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-[130px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="severity-filter"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200">
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
          <SelectTrigger className="w-[160px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="pipeline-filter"><SelectValue placeholder="Pipeline" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200">
            <SelectItem value="all">All Pipelines</SelectItem>
            {pipelineIds.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="incident-list">
        {incidents.map((inc, i) => (
          <button key={inc.incident_id} onClick={() => openDetail(inc)}
            className={`w-full text-left sf-card p-4 transition-all duration-200 ${sevBorder[inc.severity] || ""} ${selected === inc.incident_id ? "ring-1 ring-[#29B5E8]/30" : ""} animate-fade-up`}
            style={{ animationDelay: `${i * 50}ms` }}
            data-testid={`incident-card-${inc.incident_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`status-dot ${inc.severity}`} />
                  <span className="font-medium text-sm">{inc.title}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{inc.summary}</p>
                {/* Confidence Score */}
                {inc.confidence_score != null && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Confidence</span>
                    <div className="w-24"><ConfidenceBar score={inc.confidence_score} /></div>
                  </div>
                )}
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="font-mono">{inc.incident_id}</span>
                  <span className="font-mono">{inc.pipeline_id}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{inc.created_at?.slice(0, 16).replace("T", " ")}</span>
                  {inc.detection_rule && <span className="font-mono text-blue-500 dark:text-blue-400">{inc.detection_rule}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <Badge variant={inc.severity === "critical" ? "destructive" : "outline"} className="text-[10px] uppercase font-bold">{inc.severity}</Badge>
                <Badge variant="outline" className={`text-[10px] uppercase ${inc.status === "open" ? "border-red-300 text-red-600 dark:text-red-400" : inc.status === "resolved" ? "border-emerald-300 text-emerald-600 dark:text-emerald-400" : "border-amber-300 text-amber-600 dark:text-amber-400"}`}>{inc.status}</Badge>
                <button onClick={(e) => { e.stopPropagation(); copyShareLink(inc.incident_id); }} className="text-muted-foreground hover:text-primary transition-colors p-1" data-testid={`share-${inc.incident_id}`}>
                  <Share2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </button>
        ))}
        {incidents.length === 0 && <div className="text-center py-12 text-muted-foreground font-mono">[ No incidents match filters ]</div>}
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!detailData} onOpenChange={() => { setDetailData(null); setSelected(null); }}>
        <SheetContent className="w-full sm:max-w-[560px] overflow-auto" data-testid="incident-detail-sheet">
          {detailData && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`status-dot ${detailData.severity}`} />
                  <Badge variant={detailData.severity === "critical" ? "destructive" : "outline"} className="text-[10px] uppercase font-bold">{detailData.severity}</Badge>
                  <Badge variant="outline" className="text-[10px] uppercase">{detailData.status}</Badge>
                  <button onClick={() => copyShareLink(detailData.incident_id)} className="ml-auto text-muted-foreground hover:text-primary transition-colors" data-testid="share-incident-link">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
                <SheetTitle className="text-lg font-bold leading-tight">{detailData.title}</SheetTitle>
              </SheetHeader>

              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                  <span>{detailData.incident_id}</span>
                  <span>{detailData.pipeline_id}</span>
                  <span>{detailData.category}</span>
                </div>

                {/* Confidence Score */}
                {detailData.confidence_score != null && (
                  <div className="bg-muted/30 border border-border rounded-sm p-3" data-testid="detail-confidence">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Detection Confidence</span>
                      <span className="text-sm font-black font-mono">{Math.round(detailData.confidence_score * 100)}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-1000 ${detailData.confidence_score >= 0.9 ? "bg-gradient-to-r from-red-500 to-red-600" : detailData.confidence_score >= 0.7 ? "bg-gradient-to-r from-amber-500 to-amber-600" : "bg-gradient-to-r from-blue-500 to-blue-600"}`} style={{ width: `${Math.round(detailData.confidence_score * 100)}%` }} />
                    </div>
                    {detailData.detection_rule && <p className="text-[10px] text-muted-foreground font-mono mt-1.5">Rule: {detailData.detection_rule}</p>}
                  </div>
                )}

                <div>
                  <h4 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Summary</h4>
                  <p className="text-sm leading-relaxed">{detailData.summary}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-sm p-3" data-testid="ai-summary-section">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-[10px] uppercase tracking-widest font-bold text-blue-700 dark:text-blue-400 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> AI Analysis
                    </h4>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={handleGenerateSummary} disabled={aiLoading || isViewer} data-testid="generate-ai-summary-button">
                      {aiLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Generating...</> : "Generate"}
                    </Button>
                  </div>
                  <p className="text-sm leading-relaxed">{detailData.ai_summary || "Click Generate for AI-powered analysis."}</p>
                </div>

                <div>
                  <h4 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Root Cause Hypothesis</h4>
                  <p className="text-sm leading-relaxed">{detailData.root_cause_hypothesis || "---"}</p>
                </div>

                {detailData.blast_radius?.length > 0 && (
                  <div data-testid="blast-radius-section">
                    <h4 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5 flex items-center gap-1.5">
                      <GitBranch className="w-3 h-3" /> Blast Radius ({detailData.blast_radius.length} assets)
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {detailData.blast_radius.map((a) => (
                        <Badge key={a} variant="outline" className="font-mono text-[10px] border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20">{a}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Actions - hidden for viewers */}
                {detailData.status !== "resolved" && (
                  <div className="flex gap-2" data-testid="incident-actions">
                    {isViewer ? (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="w-3 h-3" /> View-only mode</div>
                    ) : (
                      <>
                        {detailData.status === "open" && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateStatus("acknowledged")} data-testid="acknowledge-incident-button">Acknowledge</Button>
                        )}
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-[#2D3142]" onClick={() => handleUpdateStatus("resolved")} data-testid="resolve-incident-button">Resolve</Button>
                      </>
                    )}
                  </div>
                )}

                <div data-testid="remediations-section">
                  <h4 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Remediation Actions</h4>
                  <div className="space-y-2">
                    {(detailData.remediations || []).map((rem) => (
                      <div key={rem.action_id} className="border border-border rounded-sm p-3 bg-card" data-testid={`remediation-card-${rem.action_id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] uppercase font-mono">{rem.action_type?.replace(/_/g, " ")}</Badge>
                            <Badge variant={rem.status === "approved" ? "secondary" : rem.status === "rejected" ? "destructive" : "outline"} className="text-[10px] uppercase">{rem.status}</Badge>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{rem.recommendation}</p>
                        {rem.status === "proposed" && (
                          isViewer ? (
                            <div className="flex items-center gap-1.5 text-[10px] text-amber-600 dark:text-amber-400"><Lock className="w-3 h-3" /> Approval requires admin access</div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input placeholder="Approval note..." value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} className="h-7 text-xs flex-1" data-testid={`approval-note-${rem.action_id}`} />
                              <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveRemediation(rem.action_id, "approve")} data-testid={`approve-remediation-${rem.action_id}`}>
                                <Check className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7 text-[10px]" onClick={() => handleApproveRemediation(rem.action_id, "reject")} data-testid={`reject-remediation-${rem.action_id}`}>
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </div>
                          )
                        )}
                        {rem.approved_by && (
                          <div className="text-[10px] text-muted-foreground mt-1.5 font-mono">{rem.status} by {rem.approved_by}{rem.approval_note && ` - "${rem.approval_note}"`}</div>
                        )}
                      </div>
                    ))}
                    {(!detailData.remediations || detailData.remediations.length === 0) && (
                      <p className="text-sm text-muted-foreground font-mono text-center py-3">[ No remediations ]</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
